/**
 * The #66 infinite query (§5.4, SPEC `saved-posts`).
 *
 * Keyed on the **full** param set — lists, tags, saved-date bounds, the §5.2 filters, the range,
 * the search term and the sort — so any change starts a new query rather than replaying a cursor
 * cut under a different ordering. That is what makes a keyset collection safe: a cursor never
 * outlives the query it belongs to.
 */

import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { MAX_FEED_LIMIT, type SavedFeedQuery, type SavedFeedResponse } from '@lw/shared';
import { getSavedFeed } from '../../api/saved';
import { QK } from '../../api/queryKeys';

export function useSavedQuery(
  params: Partial<SavedFeedQuery>,
  enabled: boolean,
): UseInfiniteQueryResult<InfiniteData<SavedFeedResponse>, Error> {
  const base: Partial<SavedFeedQuery> = { ...params, limit: MAX_FEED_LIMIT };
  return useInfiniteQuery({
    queryKey: QK.savedPage(base),
    queryFn: ({ pageParam, signal }) =>
      getSavedFeed({ ...base, cursor: pageParam ?? undefined }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}
